import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import theme from "../../theme/theme";
import {
  fetchWarehouses,
  createWarehouse,
  updateWarehouse,
  deleteWarehouse,
} from "../../api/InventoryService";

const WarehousesPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });

  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    location: "",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadWarehouses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWarehouses({ page: currentPage, per_page: perPage });
      setWarehouses(res.data.data || []);
      setLinks(res.data.links || []);
      setMeta({ from: res.data.from, to: res.data.to, total: res.data.total });
    } catch (error) {
      toast.error("Failed to fetch warehouses");
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  const handleOpenDialog = (warehouse = null) => {
    if (warehouse) {
      setFormData({
        id: warehouse.id,
        name: warehouse.name,
        location: warehouse.location,
      });
    } else {
      setFormData({ id: null, name: "", location: "" });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.location.trim()) {
      return toast.warning("Name and location are required");
    }
    setSaving(true);
    try {
      if (formData.id) {
        await updateWarehouse(formData.id, {
          name: formData.name,
          location: formData.location,
        });
        toast.success("Warehouse updated");
      } else {
        await createWarehouse({
          name: formData.name,
          location: formData.location,
        });
        toast.success("Warehouse created");
      }
      setOpenDialog(false);
      loadWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save warehouse");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (warehouse) => {
    setDeleteTarget(warehouse);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWarehouse(deleteTarget.id);
      toast.success("Warehouse deleted");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadWarehouses();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete warehouse",
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID" },
    { field: "name", headerName: "Warehouse Name" },
    { field: "location", headerName: "Location" },
  ];

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleOpenDialog(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box component="h1" sx={{ m: 0 }}>
          Warehouses
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: theme.button.borderRadius }}
        >
          Add Warehouse
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        data={warehouses}
        actions={actions}
        loading={loading}
        links={links}
        setCurrentPage={setCurrentPage}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {formData.id ? "Edit Warehouse" : "Add Warehouse"}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <TextField
            fullWidth
            label="Warehouse Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Location"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Warehouse"
        message={`Are you sure you want to delete warehouse "${deleteTarget?.name}"?`}
        loading={deleting}
      />
    </Box>
  );
};

export default WarehousesPage;
